<template>
  <div
    class="resilient-avatar-image"
    :data-avatar-state="imageState"
    :role="isLoaded || !alt ? undefined : 'img'"
    :aria-label="isLoaded || !alt ? undefined : alt"
  >
    <slot v-if="!isLoaded" name="fallback"></slot>
    <img
      v-if="requestActive && normalizedSrc"
      :key="requestVersion"
      class="resilient-avatar-image__media"
      :class="{
        'resilient-avatar-image__media--pending': !isLoaded,
      }"
      :src="normalizedSrc"
      :alt="alt"
      :aria-hidden="isLoaded ? undefined : 'true'"
      decoding="async"
      referrerpolicy="no-referrer"
      @load="handleLoad"
      @error="handleError"
    />
  </div>
</template>

<script>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";

export default {
  name: "ResilientAvatarImage",
  props: {
    src: {
      type: String,
      default: "",
    },
    alt: {
      type: String,
      default: "",
    },
    maxRetries: {
      type: Number,
      default: 6,
    },
    retryDelayMs: {
      type: Number,
      default: 1000,
    },
    maxRetryDelayMs: {
      type: Number,
      default: 8000,
    },
  },
  emits: ["load", "error"],
  setup(props, { emit }) {
    const imageState = ref("missing");
    const requestActive = ref(false);
    const requestVersion = ref(0);
    let retryCount = 0;
    let retryTimer = null;
    let isMounted = false;

    const normalizedSrc = computed(() =>
      typeof props.src === "string" ? props.src.trim() : ""
    );
    const isLoaded = computed(() => imageState.value === "loaded");

    const clearRetryTimer = () => {
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const beginAttempt = async () => {
      if (!isMounted || !normalizedSrc.value) return;

      requestActive.value = false;
      imageState.value = "loading";
      await nextTick();

      if (!isMounted || !normalizedSrc.value) return;
      requestVersion.value += 1;
      requestActive.value = true;
    };

    const scheduleRetry = () => {
      clearRetryTimer();
      const retryLimit = Number.isFinite(props.maxRetries)
        ? Math.max(0, Math.floor(props.maxRetries))
        : 0;
      if (retryCount >= retryLimit) return;

      const baseDelay = Number.isFinite(props.retryDelayMs)
        ? Math.max(0, props.retryDelayMs)
        : 1000;
      const retryDelayLimit = Number.isFinite(props.maxRetryDelayMs)
        ? Math.max(baseDelay, props.maxRetryDelayMs)
        : 8000;
      const delay = Math.min(
        baseDelay * Math.pow(2, retryCount),
        retryDelayLimit
      );
      retryCount += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        beginAttempt();
      }, delay);
    };

    const handleLoad = (event) => {
      clearRetryTimer();
      retryCount = 0;
      imageState.value = "loaded";
      emit("load", event);
    };

    const handleError = (event) => {
      requestActive.value = false;
      imageState.value = "error";
      emit("error", event);
      scheduleRetry();
    };

    const retryAfterConnectionRecovery = () => {
      if (!normalizedSrc.value || isLoaded.value) return;
      clearRetryTimer();
      retryCount = 0;
      beginAttempt();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        retryAfterConnectionRecovery();
      }
    };

    watch(
      normalizedSrc,
      (source) => {
        clearRetryTimer();
        retryCount = 0;
        requestVersion.value += 1;
        requestActive.value = Boolean(source);
        imageState.value = source ? "loading" : "missing";
      },
      { immediate: true }
    );

    onMounted(() => {
      isMounted = true;
      window.addEventListener("online", retryAfterConnectionRecovery);
      document.addEventListener("visibilitychange", handleVisibilityChange);
    });

    onBeforeUnmount(() => {
      isMounted = false;
      clearRetryTimer();
      window.removeEventListener("online", retryAfterConnectionRecovery);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    });

    return {
      handleError,
      handleLoad,
      imageState,
      isLoaded,
      normalizedSrc,
      requestActive,
      requestVersion,
    };
  },
};
</script>

<style scoped>
.resilient-avatar-image {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resilient-avatar-image__media {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
}

.resilient-avatar-image__media--pending {
  position: absolute;
  inset: 0;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
